import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { getProjects, getContactMessages } from "../../services/api";
import "../../styles/Admin.css";

const Dashboard = () => {
  const [stats, setStats] = useState({
    totalProjects: 0,
    activeProjects: 0,
    unreadMessages: 0,
    totalMessages: 0,
  });
  const [recentProjects, setRecentProjects] = useState([]);
  const [recentMessages, setRecentMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    // التحقق من تسجيل الدخول
    const token = localStorage.getItem("token");
    if (!token) {
      navigate("/admin/login");
      return;
    }

    fetchDashboardData();
  }, [navigate]);

  const fetchDashboardData = async () => {
    try {
      // جلب المشاريع
      const projectsResponse = await getProjects();
      const projectsData = Array.isArray(projectsResponse)
        ? projectsResponse
        : projectsResponse.data || [];
      const activeProjects = projectsData.filter((p) => p.status === "active");
      // جلب الرسائل
      const messagesResponse = await getContactMessages();
      const messagesData = Array.isArray(messagesResponse)
        ? messagesResponse
        : messagesResponse.data || [];
      const unreadMessages = messagesData.filter((m) => m.status === "new");
      // تحديث الإحصائيات
      setStats({
        totalProjects: projectsData.length,
        activeProjects: activeProjects.length,
        unreadMessages: unreadMessages.length,
        totalMessages: messagesData.length,
      });

      // أحدث 5 مشاريع
      setRecentProjects(projectsData.slice(0, 5));

      // أحدث 5 رسائل
      setRecentMessages(messagesData.slice(0, 5));
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("token");
    navigate("/admin/login");
  };

  if (loading) {
    return (
      <div className="admin-loading">
        <div className="spinner"></div>
        <p>جاري التحميل...</p>
      </div>
    );
  }

  return (
    <div className="admin-layout" dir="rtl">
      {/* Mobile Menu Button */}
      <button 
        className={`mobile-menu-btn ${sidebarOpen ? 'active' : ''}`}
        onClick={() => setSidebarOpen(!sidebarOpen)}
      >
        <span></span>
        <span></span>
        <span></span>
      </button>

      {/* Sidebar Overlay */}
      <div 
        className={`sidebar-overlay ${sidebarOpen ? 'active' : ''}`}
        onClick={() => setSidebarOpen(false)}
      />

      {/* Sidebar */}
      <aside className={`admin-sidebar ${sidebarOpen ? 'open' : ''}`}>
        <div className="admin-sidebar-header">
          <h2 className="admin-sidebar-logo">لوحة التحكم</h2>
          <p className="admin-sidebar-subtitle">إدارة المعرض</p>
        </div>

        <nav>
          <ul className="admin-nav">
            <li className="admin-nav-item">
              <Link to="/admin/dashboard" className="admin-nav-link active" onClick={() => setSidebarOpen(false)}>
                <span className="nav-icon">📊</span>
                <span>الرئيسية</span>
              </Link>
            </li>
          
            <li className="admin-nav-item">
              <Link to="/admin/hero" className="admin-nav-link" onClick={() => setSidebarOpen(false)}>
                <span className="nav-icon">🎯</span>
                <span>القسم الرئيسي</span>
              </Link>
            </li>
            <li className="admin-nav-item">
              <Link to="/admin/about" className="admin-nav-link" onClick={() => setSidebarOpen(false)}>
                <span className="nav-icon">👤</span>
                <span>نبذة عني</span>
              </Link>
            </li>
            <li className="admin-nav-item">
              <Link to="/admin/projects" className="admin-nav-link" onClick={() => setSidebarOpen(false)}>
                <span className="nav-icon">🏗️</span>
                <span>المشاريع</span>
              </Link>
            </li>
            <li className="admin-nav-item">
              <Link to="/admin/contact" className="admin-nav-link" onClick={() => setSidebarOpen(false)}>
                <span className="nav-icon">📧</span>
                <span>معلومات التواصل</span>
              </Link>
            </li>
            <li className="admin-nav-item">
              <Link to="/admin/change-password" className="admin-nav-link" onClick={() => setSidebarOpen(false)}>
                <span className="nav-icon">🔒</span>
                <span>تغيير كلمة المرور</span>
              </Link>
            </li>
            <li className="admin-nav-item">
              <a
                href="/"
                className="admin-nav-link"
                target="_blank"
                rel="noopener noreferrer"
              >
                <span className="nav-icon">🌐</span>
                <span>عرض الموقع</span>
              </a>
            </li>
          </ul>
        </nav>

        <div className="admin-sidebar-footer">
          <button className="logout-btn" onClick={handleLogout}>
            <span>🚪</span>
            تسجيل الخروج
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="admin-main">
        <div className="admin-header">
          <h1>لوحة التحكم</h1>
          <p>مرحباً بك! إليك نظرة عامة على معرض أعمالك</p>
        </div>

        {/* Stats Cards */}
        <div className="dashboard-stats">
          <div className="stat-card stat-primary">
            <div className="stat-icon">🏗️</div>
            <div className="stat-details">
              <h3>{stats.totalProjects}</h3>
              <p>إجمالي المشاريع</p>
            </div>
          </div>

          <div className="stat-card stat-success">
            <div className="stat-icon">✅</div>
            <div className="stat-details">
              <h3>{stats.activeProjects}</h3>
              <p>المشاريع النشطة</p>
            </div>
          </div>

          <div className="stat-card stat-warning">
            <div className="stat-icon">📩</div>
            <div className="stat-details">
              <h3>{stats.unreadMessages}</h3>
              <p>الرسائل غير المقروءة</p>
            </div>
          </div>

          <div className="stat-card stat-info">
            <div className="stat-icon">💬</div>
            <div className="stat-details">
              <h3>{stats.totalMessages}</h3>
              <p>إجمالي الرسائل</p>
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="dashboard-section">
          <h2 className="section-title">إجراءات سريعة</h2>
          <div className="quick-actions">
            <Link to="/admin/hero" className="action-card">
              <span className="action-icon">🎯</span>
              <h3>تعديل القسم الرئيسي</h3>
              <p>تحديث بانر الصفحة الرئيسية</p>
            </Link>

            <Link to="/admin/about" className="action-card">
              <span className="action-icon">👤</span>
              <h3>تعديل النبذة</h3>
              <p>تحديث معلوماتك الشخصية</p>
            </Link>

            <Link to="/admin/projects" className="action-card">
              <span className="action-icon">➕</span>
              <h3>إضافة مشروع</h3>
              <p>إنشاء مشروع جديد</p>
            </Link>

            <Link to="/admin/contact" className="action-card">
              <span className="action-icon">📧</span>
              <h3>معلومات التواصل</h3>
              <p>تحديث بيانات الاتصال</p>
            </Link>
          </div>
        </div>

        {/* Recent Projects */}
        <div className="dashboard-section">
          <div className="section-header-flex">
            <h2 className="section-title">أحدث المشاريع</h2>
            <Link to="/admin/projects" className="view-all-link">
              عرض الكل ←
            </Link>
          </div>

          {recentProjects.length === 0 ? (
            <div className="empty-state">
              <span className="empty-icon">📁</span>
              <p>لا توجد مشاريع بعد</p>
              <Link to="/admin/projects" className="btn-primary">
                أضف مشروعك الأول
              </Link>
            </div>
          ) : (
            <div className="dashboard-table">
              <table>
                <thead>
                  <tr>
                    <th>المشروع</th>
                    <th>التصنيف</th>
                    <th>الموقع</th>
                    <th>السنة</th>
                    <th>الحالة</th>
                  </tr>
                </thead>
                <tbody>
                  {recentProjects.map((project) => (
                    <tr key={project.id}>
                      <td className="project-title-cell">
                        <strong>{project.title}</strong>
                      </td>
                      <td>{project.category || "-"}</td>
                      <td>{project.location || "-"}</td>
                      <td>{project.year || "-"}</td>
                      <td>
                        <span className={`status-badge ${project.status}`}>
                          {project.status === "active" ? "نشط" : project.status === "completed" ? "مكتمل" : project.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Recent Messages */}
        <div className="dashboard-section">
          <div className="section-header-flex">
            <h2 className="section-title">أحدث الرسائل</h2>
            {stats.unreadMessages > 0 && (
              <span className="badge badge-warning">
                {stats.unreadMessages} جديدة
              </span>
            )}
          </div>

          {recentMessages.length === 0 ? (
            <div className="empty-state">
              <span className="empty-icon">📭</span>
              <p>لا توجد رسائل بعد</p>
            </div>
          ) : (
            <div className="messages-list">
              {recentMessages.map((message) => (
                <div
                  key={message.id}
                  className={`message-card ${
                    message.status === "new" ? "unread" : ""
                  }`}
                >
                  <div className="message-header">
                    <div className="message-from">
                      <strong>{message.name}</strong>
                      {message.status === "new" && (
                        <span className="new-badge">جديدة</span>
                      )}
                    </div>
                    <span className="message-date">
                      {new Date(message.created_at).toLocaleDateString('ar-SA')}
                    </span>
                  </div>
                  <div className="message-email">{message.email}</div>
                  <div className="message-preview">
                    {message.message.substring(0, 100)}
                    {message.message.length > 100 && "..."}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default Dashboard;
